import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import React, { memo, lazy, Suspense, useState } from 'react';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { drawerFooter } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/kycDocumentDrawer.styled';
import { Button, Divider, Drawer, Modal, Text } from '@mantine/core';
import { DocumentTypeBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/DocumentTypeBadge.tsx';
import { TextInput } from 'react-hook-form-mantine';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

import {
	CREATE_KYC_DOCUMENT_FORM_FIELDS,
	useCreateKycDocumentDrawerController,
} from './useCreateKycDocumentDrawerController';
import {
	DocumentWorkflowTemplateSourceFormat,
	CustomDetailCategory,
	DataCustomDetailTct,
	DataFileTct,
} from '@dealstack/shared/lib/generated';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';

import { KycPlacementDraftProvider } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/KycPlacementDraftContext.tsx';

// Lazy: the editor pulls in react-pdf and the whole document surface, and it only ever
// renders inside a modal the admin opens.
const KycFieldBindingEditor = lazy(() =>
	import(
		'@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/FieldBinding/KycFieldBindingEditor.tsx'
	).then((module) => ({ default: module.KycFieldBindingEditor }))
);

interface Props {
	opened: boolean;
	onClose: () => void;
	file: DataFileTct;
	type: DataCustomDetailTct['type'];
	category?: CustomDetailCategory | null;
	addFormTemplate: (formTemplate: FormTemplate) => void;
}

const CreateKycDocumentDrawer = ({
	opened,
	onClose,
	file,
	type,
	category,
	addFormTemplate,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();

	const { editKycDocumentForm, onSubmit, onPlacementSaved, pendingPlacement, showStampMetadata } =
		useCreateKycDocumentDrawerController({
			file,
			type,
			category,
			addFormTemplate,
			onClose,
		});
	const [placementEditorOpen, setPlacementEditorOpen] = useState(false);

	const requiresSignature = editKycDocumentForm.watch(
		CREATE_KYC_DOCUMENT_FORM_FIELDS.requiresSignature
	);
	const useManualSignaturePlacement = editKycDocumentForm.watch(
		CREATE_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement
	);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				<Text size="B4Bold">
					{tMipCreate('kyc.documents.create_drawer.complete_upload')}
				</Text>
			}
		>
			<Text size="B5Medium" mb="0.4rem">
				{tMipCreate('kyc.documents.create_drawer.file_name')}
			</Text>
			<Text size="B6Medium" c="gray.9" mb="0.8rem">
				{file.name}
			</Text>

			<DocumentTypeBadge
				text={
					type === CustomDetailEnum.INDIVIDUAL
						? tMipCreate('kyc.documents.edit_drawer.individual_investor')
						: tMipCreate('kyc.documents.edit_drawer.piv_investor')
				}
				iconVariant="folder"
			/>

			<Divider mt="1rem" mb="2rem" />

			<form onSubmit={editKycDocumentForm.handleSubmit(onSubmit)}>
				<Text size="B5Medium" mb="0.4rem">
					{tMipCreate('kyc.documents.create_drawer.name_your_document')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{tMipCreate('kyc.documents.create_drawer.name_your_document_description')}
				</Text>
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_FORM_NAME_INPUT}>
					<TextInput
						name={CREATE_KYC_DOCUMENT_FORM_FIELDS.name}
						control={editKycDocumentForm.control}
						size="md"
						aria-label={tMipCreate('kyc.documents.create_drawer.name_your_document')}
						placeholder={tMipCreate('kyc.documents.create_drawer.enter_file_name')}
						mb="2rem"
					/>
				</UIElementIdentifier>

				<Text size="B5Medium" mb="0.4rem">
					{tMipCreate('kyc.documents.create_drawer.instructions_to_investors')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{tMipCreate(
						'kyc.documents.create_drawer.instructions_to_investors_description'
					)}
				</Text>
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_FORM_INSTRUCTION_INPUT}
				>
					<TextInput
						name={CREATE_KYC_DOCUMENT_FORM_FIELDS.instructions}
						control={editKycDocumentForm.control}
						size="md"
						placeholder={tMipCreate(
							'kyc.documents.create_drawer.enter_your_instructions_here'
						)}
					/>
				</UIElementIdentifier>

				<Text size="B5Medium" mt="2rem" mb="0.4rem">
					{tMipCreate('kyc.documents.create_drawer.require_electronic_signature')}
				</Text>
				<Text size="B6Medium" c="gray.9" mb="0.8rem">
					{tMipCreate('kyc.documents.create_drawer.require_electronic_signature_desc')}
				</Text>
				<AdvancedSwitch
					selectedValue={requiresSignature}
					onChange={(value) =>
						editKycDocumentForm.setValue(
							CREATE_KYC_DOCUMENT_FORM_FIELDS.requiresSignature,
							!!value
						)
					}
					aria-label={tMipCreate(
						'kyc.documents.create_drawer.require_electronic_signature'
					)}
				/>

				{requiresSignature && (
					<>
						<Text size="B5Medium" mt="2rem" mb="0.4rem">
							{tMipCreate('kyc.documents.create_drawer.place_signatures_manually')}
						</Text>
						<Button
							variant="default"
							fullWidth
							onClick={() => setPlacementEditorOpen(true)}
						>
							{tMipCreate(
								useManualSignaturePlacement
									? 'kyc.documents.create_drawer.edit_placement'
									: 'kyc.documents.create_drawer.open_placement_editor'
							)}
						</Button>
					</>
				)}
			</form>

			{placementEditorOpen && (
				<Modal
					opened
					fullScreen
					withCloseButton={false}
					padding={0}
					onClose={() => setPlacementEditorOpen(false)}
				>
					<Suspense fallback={null}>
						{/* The form does not exist yet, so there is nothing to bind fields
						    against: the editor shows the signature job alone, and the box
						    comes back here to ride along with the create. */}
						<KycPlacementDraftProvider
							onDraft={onPlacementSaved}
							pending={pendingPlacement}
						>
							<KycFieldBindingEditor
								templateId={null}
								fileId={file.id}
								sourceFormat={DocumentWorkflowTemplateSourceFormat.PDF}
								// Nothing to bind on a form that is not saved yet.
								supportsBindings={false}
								requiresSignature={!!requiresSignature}
								initialShowStampMetadata={showStampMetadata}
								onClose={() => setPlacementEditorOpen(false)}
							/>
						</KycPlacementDraftProvider>
					</Suspense>
				</Modal>
			)}

			<div style={drawerFooter}>
				<Divider mb="1rem" />

				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_DRAWER_SAVE_BUTTON}
				>
					<Button
						onClick={editKycDocumentForm.handleSubmit(onSubmit)}
						fullWidth
						mb="1rem"
					>
						{tMipCreate('kyc.documents.create_drawer.save')}
					</Button>
				</UIElementIdentifier>

				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_KYC_DRAWER_CANCEL_BUTTON}
				>
					<Button onClick={onClose} fullWidth variant="default">
						{tMipCreate('kyc.documents.create_drawer.cancel')}
					</Button>
				</UIElementIdentifier>
			</div>
		</Drawer>
	);
};
const _CreateKycDocumentDrawerExport = memo(CreateKycDocumentDrawer);

export { _CreateKycDocumentDrawerExport as CreateKycDocumentDrawer };
